import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { SignupForm } from '@/components/auth/SignupForm';
import { useInvitePreview } from '@/hooks/useInvitePreview';

export function SignupPage() {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? null;
  // When the form enters the post-signup "check your email" state, hide the
  // surrounding page chrome (back link, page heading, footer login link) so
  // the confirmation panel stands alone.
  const [pendingVerification, setPendingVerification] = useState(false);

  const [params] = useSearchParams();
  const rawNext = params.get('next');
  const next =
    rawNext &&
    (rawNext.startsWith('/accept_group_invite/') ||
      rawNext.startsWith('/join/'))
      ? rawNext
      : null;
  const inviteToken = params.get('inviteToken');
  const invitedEmail = params.get('email');

  // Preview fetch is best-effort: if the token is invalid/expired we still
  // render the signup form, just without the "joining {groupName}" banner.
  const previewQuery = useInvitePreview(inviteToken);
  const preview = previewQuery.data?.preview;

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col px-6 py-8">
      {pendingVerification ? null : (
        <Link to="/" className="mb-10 flex items-center gap-2 text-sm text-[color:var(--color-fg-muted)]">
          <span aria-hidden>←</span> Back
        </Link>
      )}
      <div className="flex flex-1 flex-col justify-center gap-8">
        {pendingVerification ? null : (
          <header className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold">Create account</h1>
            <p className="text-sm text-[color:var(--color-fg-muted)]">
              Join the pool. Prove you know football better than your friends.
            </p>
          </header>
        )}

        {!pendingVerification && preview ? (
          <div
            style={{
              padding: '14px 16px',
              borderRadius: 12,
              background: 'var(--hot-soft)',
              border: '1px solid var(--hot)',
              color: 'var(--fg)',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            You're signing up to join{' '}
            <b style={{ color: 'var(--hot)' }}>{preview.groupName}</b>.
          </div>
        ) : null}

        <SignupForm
          googleClientId={googleClientId || null}
          onPendingVerificationChange={setPendingVerification}
          next={next}
          inviteToken={inviteToken}
          invitedEmail={invitedEmail}
        />

        {pendingVerification ? null : (
          <p className="text-center text-sm text-[color:var(--color-fg-muted)]">
            Already have an account?{' '}
            <Link
              to={
                next
                  ? `/login?${new URLSearchParams({ next }).toString()}`
                  : '/login'
              }
              className="text-[color:var(--color-fg)] underline underline-offset-2"
            >
              Log in
            </Link>
          </p>
        )}
      </div>
    </main>
  );
}
